import React, { useState, useEffect } from 'react';
import { 
  ChartBarIcon, 
  MapIcon,
  ShieldExclamationIcon,
  FlagIcon,
  BuildingOffice2Icon,
  HomeModernIcon
  // AdjustmentsHorizontalIcon // HIDDEN: Reserved for future use
} from '@heroicons/react/24/outline';
import { Card, CardContent } from '@/components/ui/Card';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/state/language/LanguageProvider';
import { useAuth } from '@/state/auth/AuthProvider';
import { KeyRisksWidget } from '@/components/dashboard/KeyRisksWidget';
import { KeyIssuesWidget } from '@/components/dashboard/KeyIssuesWidget';
import { KeyActionsWidget } from '@/components/dashboard/KeyActionsWidget';
import { PortfolioDashboardPanel } from '@/components/dashboard/PortfolioDashboardPanel';
import { DashboardAgendaWidget } from '@/components/dashboard/DashboardAgendaWidget';
// Removed RaiseRiskWidget import - moved to risk management page
// Removed ActivityFeed import - removed from dashboard
import { dashboardService, DashboardMetrics } from '@/services/dashboardService';
import {
  pePortfolioService,
  type PePortfolioDashboardBundleDto,
  type PePortfolioWorkspaceDto,
} from '@/services/pePortfolioService';
// import { dashboardPreferencesService } from '@/services/dashboardPreferencesService'; // HIDDEN: Reserved for future use
import { SetupReminder } from '@/components/setup-wizard/SetupReminder';
import { SetupWizardModal } from '@/components/setup-wizard/SetupWizardModal';
import { setupWizardService } from '@/services/setupWizardService';
import { tWithFallback } from '@/utils/tWithFallback';

/** Tenant names are sometimes stored as "Company (admin@firm.com)" for disambiguation; dashboard badge should show company only. */
function tenantLabelForDashboard(name: string): string {
  const trimmed = name.trim();
  const withoutEmailSuffix = trimmed.replace(/\s*\([^)]*@[^)]+\)\s*$/u, '').trim();
  return withoutEmailSuffix.length > 0 ? withoutEmailSuffix : trimmed;
}

// Dashboard customization interfaces
interface DashboardWidget {
  id: string;
  name: string;
  component: string;
  enabled: boolean;
  position: { row: number; col: number };
  size: 'small' | 'medium' | 'large';
}

// DashboardLayout interface - HIDDEN: Reserved for future use
// interface DashboardLayout {
//   id: string;
//   name: string;
//   description: string;
//   gridCols: number;
//   gridRows: number;
// }

interface LocalDashboardPreferences {
  layout: string;
  widgets: DashboardWidget[];
  spacing: 'compact' | 'comfortable' | 'spacious';
}

export const DashboardPage: React.FC = () => {
  const { t } = useLanguage();
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState<DashboardMetrics>({
    activeRoadmaps: 0,
    activeTransformations: 0,
    activeRisks: 0,
    inchstonesCompleted: 0,
    inchstonesPlanned: 0
  });
  const [metricsLoading, setMetricsLoading] = useState(true);

  /** PE portfolio dashboard: second tab + company scope (only when `PORTFOLIO` + viewPortfolio). */
  const [dashboardTab, setDashboardTab] = useState<'firm' | 'portfolio'>('firm');
  const [peWorkspace, setPeWorkspace] = useState<PePortfolioWorkspaceDto | null>(null);
  const [peWorkspaceLoaded, setPeWorkspaceLoaded] = useState(false);
  const [portfolioCompanies, setPortfolioCompanies] = useState<Array<{ id: string; name: string }>>([]);
  const [portfolioCompaniesLoading, setPortfolioCompaniesLoading] = useState(false);
  const [portfolioCompanyId, setPortfolioCompanyId] = useState<string>('__all__');
  const [portfolioBundle, setPortfolioBundle] = useState<PePortfolioDashboardBundleDto | null>(null);
  const [portfolioDashboardLoading, setPortfolioDashboardLoading] = useState(false);
  const [portfolioDashboardError, setPortfolioDashboardError] = useState<string | null>(null);

  // Dashboard customization state - HIDDEN: Reserved for future use
  // const [isCustomizing, setIsCustomizing] = useState(false);
  const [dashboardPreferences] = useState<LocalDashboardPreferences>({
    layout: '2x2',
    spacing: 'comfortable',
      widgets: [
      { id: 'key_risks', name: 'Key Risks', component: 'KeyRisks', enabled: true, position: { row: 1, col: 1 }, size: 'medium' },
      { id: 'key_issues', name: 'Key Issues', component: 'KeyIssues', enabled: true, position: { row: 1, col: 2 }, size: 'medium' },
      { id: 'key_actions', name: 'Key Actions', component: 'KeyActions', enabled: true, position: { row: 1, col: 3 }, size: 'medium' },
      { id: 'business_performance', name: 'Performance Dashboard', component: 'BusinessPerformance', enabled: true, position: { row: 2, col: 1 }, size: 'large' },
      { id: 'stats_grid', name: 'Statistics Grid', component: 'StatsGrid', enabled: true, position: { row: 0, col: 1 }, size: 'large' }
    ]
  });
  // const [preferencesLoading, setPreferencesLoading] = useState(false); // HIDDEN: Reserved for future use

  // Setup wizard state
  const [showSetupWizard, setShowSetupWizard] = useState(false);
  const [setupType, setSetupType] = useState<'TENANT_ADMIN' | 'TENANT_USER' | 'INVITED_USER'>('TENANT_USER');
  const [shouldShowSetupWizard, setShouldShowSetupWizard] = useState(false);

  // Available layouts - HIDDEN: Reserved for future use
  // const availableLayouts: DashboardLayout[] = [
  //   { id: '2x2', name: '2x2 Grid', description: 'Four widgets in a 2x2 grid', gridCols: 2, gridRows: 2 },
  //   { id: '3x1', name: '3x1 Row', description: 'Three widgets in a horizontal row', gridCols: 3, gridRows: 1 },
  //   { id: '1x4', name: '1x4 Column', description: 'Four widgets in a vertical column', gridCols: 1, gridRows: 4 },
  //   { id: 'custom', name: 'Custom Grid', description: 'Flexible grid layout', gridCols: 4, gridRows: 3 }
  // ];


  // Dashboard customization functions - HIDDEN: Reserved for future use
  // const toggleCustomization = () => {
  //   setIsCustomizing(!isCustomizing);
  // };

  // const saveDashboardPreferences = async () => {
  //   try {
  //     setPreferencesLoading(true);
  //     await dashboardPreferencesService.savePreferences(dashboardPreferences);
  //     setIsCustomizing(false);
  //     // TODO: Show success toast notification
  //   } catch (error) {
  //     console.error('Error saving dashboard preferences:', error);
  //     // TODO: Show error toast notification
  //   } finally {
  //     setPreferencesLoading(false);
  //   }
  // };

  // const resetDashboardPreferences = async () => {
  //   try {
  //     setPreferencesLoading(true);
  //     const defaultPreferences = await dashboardPreferencesService.resetPreferences();
  //     setDashboardPreferences(defaultPreferences);
  //     // TODO: Show success toast notification
  //   } catch (error) {
  //     console.error('Error resetting dashboard preferences:', error);
  //     // TODO: Show error toast notification
  //   } finally {
  //     setPreferencesLoading(false);
  //   }
  // };

  // Dashboard customization helper functions - HIDDEN: Reserved for future use
  // const toggleWidget = (widgetId: string) => {
  //   setDashboardPreferences(prev => ({
  //     ...prev,
  //     widgets: prev.widgets.map(widget => 
  //       widget.id === widgetId 
  //         ? { ...widget, enabled: !widget.enabled }
  //         : widget
  //     )
  //   }));
  // };

  // const updateLayout = (layoutId: string) => {
  //   setDashboardPreferences(prev => ({
  //     ...prev,
  //     layout: layoutId
  //   }));
  // };

  // const updateSpacing = (spacing: 'compact' | 'comfortable' | 'spacious') => {
  //   setDashboardPreferences(prev => ({
  //     ...prev,
  //     spacing
  //   }));
  // };

  // Helper functions for dynamic layout
  // Layout helper functions - HIDDEN: Reserved for future use (using fixed grid layout now)
  // const getSpacingClass = (spacing: string) => {
  //   switch (spacing) {
  //     case 'compact': return 'gap-1';
  //     case 'spacious': return 'gap-12';
  //     default: return 'gap-6'; // comfortable
  //   }
  // };

  // const getLayoutClass = (layout: string) => {
  //   switch (layout) {
  //     case '3x1': return 'grid-cols-1 md:grid-cols-3';
  //     case '1x4': return 'grid-cols-1';
  //     case 'custom': return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4';
  //     default: return 'grid-cols-1 md:grid-cols-2'; // 2x2
  //   }
  // };

  const isWidgetEnabled = (widgetId: string) => {
    const widget = dashboardPreferences.widgets.find(w => w.id === widgetId);
    return widget ? widget.enabled : true;
  };

  useEffect(() => {
    // Only load data if user is authenticated and we have a valid token
    const token = localStorage.getItem('auth_token');
    if (isAuthenticated && user && token) {
      loadMetrics();
      // loadDashboardPreferences(); // HIDDEN: Reserved for future use
      checkSetupStatus();
    } else {
      setMetricsLoading(false);
    }
  }, [isAuthenticated, user]);

  const showPortfolioTab =
    user?.tenantType === 'PORTFOLIO' && peWorkspaceLoaded && peWorkspace?.viewPortfolio === true;

  useEffect(() => {
    if (user?.tenantType !== 'PORTFOLIO') {
      setDashboardTab('firm');
    }
  }, [user?.tenantType]);

  useEffect(() => {
    if (user?.tenantType !== 'PORTFOLIO') {
      setPeWorkspace(null);
      setPeWorkspaceLoaded(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setPeWorkspaceLoaded(false);
      try {
        const ws = await pePortfolioService.getWorkspace();
        if (!cancelled) setPeWorkspace(ws);
      } catch {
        if (!cancelled) setPeWorkspace(null);
      } finally {
        if (!cancelled) setPeWorkspaceLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.tenantType]);

  useEffect(() => {
    if (!showPortfolioTab) return;
    let cancelled = false;
    (async () => {
      setPortfolioCompaniesLoading(true);
      try {
        const list = await pePortfolioService.listCompanies();
        if (cancelled) return;
        const rows = list
          .filter((m) => m.status === 'active')
          .map((m) => ({
            id: m.childTenantId,
            name: (m.childTenantName || m.childTenantId).trim(),
          }));
        setPortfolioCompanies(rows);
      } catch {
        if (!cancelled) setPortfolioCompanies([]);
      } finally {
        if (!cancelled) setPortfolioCompaniesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showPortfolioTab]);

  useEffect(() => {
    if (portfolioCompanyId === '__all__') return;
    if (!portfolioCompanies.some((c) => c.id === portfolioCompanyId)) {
      setPortfolioCompanyId('__all__');
    }
  }, [portfolioCompanies, portfolioCompanyId]);

  useEffect(() => {
    if (!showPortfolioTab || dashboardTab !== 'portfolio') return;
    if (portfolioCompaniesLoading) return;
    if (portfolioCompanies.length === 0) return;
    let cancelled = false;
    (async () => {
      setPortfolioDashboardLoading(true);
      setPortfolioDashboardError(null);
      try {
        const childId = portfolioCompanyId === '__all__' ? undefined : portfolioCompanyId;
        const data = await pePortfolioService.getPortfolioDashboard(childId, 3);
        if (!cancelled) setPortfolioBundle(data);
      } catch (e: unknown) {
        if (!cancelled) {
          setPortfolioBundle(null);
          const msg =
            e &&
            typeof e === 'object' &&
            e !== null &&
            'response' in e &&
            typeof (e as { response?: { data?: { message?: string } } }).response?.data?.message === 'string'
              ? (e as { response: { data: { message: string } } }).response.data.message
              : e instanceof Error
                ? e.message
                : undefined;
          setPortfolioDashboardError(
            msg ?? tWithFallback(t, 'pePortfolio.dashboard.loadError', 'Could not load portfolio dashboard.')
          );
        }
      } finally {
        if (!cancelled) setPortfolioDashboardLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    showPortfolioTab,
    dashboardTab,
    portfolioCompanyId,
    portfolioCompaniesLoading,
    portfolioCompanies.length,
    t,
  ]);

  const retryPortfolioDashboard = () => {
    if (!showPortfolioTab || dashboardTab !== 'portfolio' || portfolioCompanies.length === 0) return;
    setPortfolioDashboardError(null);
    setPortfolioDashboardLoading(true);
    const childId = portfolioCompanyId === '__all__' ? undefined : portfolioCompanyId;
    pePortfolioService
      .getPortfolioDashboard(childId, 3)
      .then((data) => {
        setPortfolioBundle(data);
        setPortfolioDashboardError(null);
      })
      .catch((e: unknown) => {
        setPortfolioBundle(null);
        const msg =
          e &&
          typeof e === 'object' &&
          e !== null &&
          'response' in e &&
          typeof (e as { response?: { data?: { message?: string } } }).response?.data?.message === 'string'
            ? (e as { response: { data: { message: string } } }).response.data.message
            : e instanceof Error
              ? e.message
              : undefined;
        setPortfolioDashboardError(
          msg ?? tWithFallback(t, 'pePortfolio.dashboard.loadError', 'Could not load portfolio dashboard.')
        );
      })
      .finally(() => setPortfolioDashboardLoading(false));
  };

  // Check if setup wizard should be shown
  const checkSetupStatus = async () => {
    try {
      if (!user?.id) return;

      // Determine setup type based on user roles
      // Check if user is tenant admin (T_ADMIN role)
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
      if (token) {
        try {
          const rolesResponse = await fetch(`/api/v1/users/${user.id}/roles`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          if (rolesResponse.ok) {
            const contentType = rolesResponse.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
              const rolesData = await rolesResponse.json();
              const isTenantAdmin = rolesData.data?.some((role: any) => 
                role.role_code === 'T_ADMIN' || role.role_name === 'Tenant Administrator'
              ) || false;

              const setupTypeToCheck = isTenantAdmin ? 'TENANT_ADMIN' : 'TENANT_USER';
              setSetupType(setupTypeToCheck);

              // Check if tenant setup is required (blocking for tenant admin)
              if (isTenantAdmin) {
                const tenantSetupComplete = await setupWizardService.isTenantSetupComplete();
                if (!tenantSetupComplete) {
                  setShouldShowSetupWizard(true);
                  setShowSetupWizard(true);
                  return;
                }
              }

              // Check user setup progress
              const userProgress = await setupWizardService.getUserSetupProgress(setupTypeToCheck);
              if (userProgress && !userProgress.is_complete) {
                // Check if setup was started within the last month
                const startedAt = new Date(userProgress.started_at);
                const oneMonthAgo = new Date();
                oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);

                if (startedAt >= oneMonthAgo) {
                  setShouldShowSetupWizard(false); // Show reminder instead
                }
              }
            }
          }
        } catch (error) {
          console.error('Error checking user roles:', error);
        }
      }
    } catch (error) {
      console.error('Error checking setup status:', error);
    }
  };

  // Dashboard preferences loading - HIDDEN: Reserved for future use
  // const loadDashboardPreferences = async () => {
  //   try {
  //     setPreferencesLoading(true);
  //     const preferences = await dashboardPreferencesService.getPreferences();
  //     setDashboardPreferences(preferences);
  //   } catch (error) {
  //     console.error('Error loading dashboard preferences:', error);
  //     // Use default preferences if loading fails
  //   } finally {
  //     setPreferencesLoading(false);
  //   }
  // };

  const loadMetrics = async () => {
    try {
      setMetricsLoading(true);
      const metricsData = await dashboardService.getMetrics();
      setMetrics(metricsData);
    } catch (error) {
      console.error('Error loading dashboard metrics:', error);
    } finally {
      setMetricsLoading(false);
    }
  };

  // Dynamic stats based on real metrics with navigation links
  const stats = [
    { 
      name: t('dashboard.activeRoadmapsCount'), 
      value: metricsLoading ? '...' : (metrics.activeRoadmaps || 0).toString(), 
      change: '+0%',
      changeType: 'neutral',
      icon: MapIcon,
      color: 'blue',
      link: '/tenant/roadmaps'
    },
    { 
      name: t('dashboard.activeTransformationsCount'), 
      value: metricsLoading ? '...' : (metrics.activeTransformations || 0).toString(), 
      change: '+0%',
      changeType: 'neutral',
      icon: ChartBarIcon,
      color: 'green',
      link: '/transformations'
    },
    { 
      name: t('dashboard.activeRisksCount'), 
      value: metricsLoading ? '...' : (metrics.activeRisks || 0).toString(), 
      change: '+0%',
      changeType: 'neutral',
      icon: ShieldExclamationIcon,
      color: 'red',
      link: '/risk'
    },
    { 
      name: t('dashboard.inchstonesProgress'), 
      value: metricsLoading ? '...' : `${metrics.inchstonesCompleted || 0}/${metrics.inchstonesPlanned || 0}`, 
      change: (metrics.inchstonesPlanned || 0) > 0 ? `${Math.round(((metrics.inchstonesCompleted || 0) / (metrics.inchstonesPlanned || 1)) * 100)}%` : '0%',
      changeType: 'positive',
      icon: FlagIcon,
      color: 'purple',
      link: '/transformations' // Inchstones are part of transformations - links to transformations page
    },
  ];

  return (
    <div className="min-h-screen bg-[var(--color-secondary-bg)]">
      <div className="container mx-auto px-2 py-2">
        {/* Setup Reminder */}
        {!shouldShowSetupWizard && (
          <SetupReminder
            setupType={setupType}
            onDismiss={() => {}}
          />
        )}

        <div className="bg-[var(--color-surface-bg)] border-0 shadow-none p-4">
          <div className="mb-2">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center space-x-2">
                <h1 className="text-xl font-bold text-[var(--color-surface-text)]">
              {t('dashboard.welcome')}
            </h1>
            {user?.tenantName && (
              <div className="flex items-center space-x-2 px-3 py-1 bg-[var(--color-accent-bg)] rounded-full">
                <div className="w-2 h-2 bg-[var(--color-accent-text)] rounded-full"></div>
                <span className="text-sm font-medium text-[var(--color-accent-text)]">
                  {tenantLabelForDashboard(user.tenantName)}
                </span>
              </div>
            )}
          </div>
          
          {/* Dashboard Customization Controls - HIDDEN: Reserved for future use */}
          {/* <div className="flex items-center space-x-2">
            {!isCustomizing ? (
              <button
                onClick={toggleCustomization}
                className="flex items-center space-x-2 px-4 py-2 bg-[var(--color-accent-bg)] text-[var(--color-accent-text)] rounded-lg hover:bg-opacity-80 transition-colors"
              >
                <PencilIcon className="h-4 w-4" />
                <span>Customize Dashboard</span>
              </button>
            ) : (
              <div className="flex items-center space-x-2">
                <button
                  onClick={saveDashboardPreferences}
                  disabled={preferencesLoading}
                  className="flex items-center space-x-2 px-4 py-2 bg-[var(--color-success-bg)] text-[var(--color-success-text)] rounded-lg hover:bg-opacity-80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <CheckIcon className="h-4 w-4" />
                  <span>{preferencesLoading ? 'Saving...' : 'Save'}</span>
                </button>
                <button
                  onClick={resetDashboardPreferences}
                  disabled={preferencesLoading}
                  className="flex items-center space-x-2 px-4 py-2 bg-[var(--color-warning-bg)] text-[var(--color-warning-text)] rounded-lg hover:bg-opacity-80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <XMarkIcon className="h-4 w-4" />
                  <span>{preferencesLoading ? 'Resetting...' : 'Reset'}</span>
                </button>
                <button
                  onClick={toggleCustomization}
                  className="flex items-center space-x-2 px-4 py-2 bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] rounded-lg hover:bg-opacity-80 transition-colors"
                >
                  <XMarkIcon className="h-4 w-4" />
                  <span>Cancel</span>
                </button>
              </div>
            )}
          </div> */}
            </div>
          </div>

          {showPortfolioTab && (
            <div
              className="mb-3 flex w-full max-w-md rounded-lg border border-[var(--color-border)] bg-[var(--color-tab-bar-bg)] p-1 shadow-[inset_0_1px_2px_var(--color-shadow)]"
              role="tablist"
              aria-label={tWithFallback(t, 'dashboard.aria.dashboardTabs', 'Dashboard view')}
            >
              <button
                type="button"
                role="tab"
                aria-selected={dashboardTab === 'firm'}
                className={`flex min-h-[2.5rem] flex-1 items-center justify-center gap-2 rounded-md px-3 py-2 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary-text)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-tab-bar-bg)] ${
                  dashboardTab === 'firm'
                    ? 'bg-[var(--color-tab-active-bg)] font-semibold text-[var(--color-tab-active-text)] shadow-sm ring-1 ring-[var(--color-border)]'
                    : 'font-medium text-[var(--color-tab-inactive-text)] hover:bg-[var(--color-hover-bg)] hover:text-[var(--color-tab-active-text)]'
                }`}
                onClick={() => setDashboardTab('firm')}
              >
                <HomeModernIcon className="h-4 w-4 shrink-0 stroke-[2]" aria-hidden />
                {tWithFallback(t, 'dashboard.tabMyFirm', 'My firm')}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={dashboardTab === 'portfolio'}
                className={`flex min-h-[2.5rem] flex-1 items-center justify-center gap-2 rounded-md px-3 py-2 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary-text)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-tab-bar-bg)] ${
                  dashboardTab === 'portfolio'
                    ? 'bg-[var(--color-tab-active-bg)] font-semibold text-[var(--color-tab-active-text)] shadow-sm ring-1 ring-[var(--color-border)]'
                    : 'font-medium text-[var(--color-tab-inactive-text)] hover:bg-[var(--color-hover-bg)] hover:text-[var(--color-tab-active-text)]'
                }`}
                onClick={() => setDashboardTab('portfolio')}
              >
                <BuildingOffice2Icon className="h-4 w-4 shrink-0 stroke-[2]" aria-hidden />
                {tWithFallback(t, 'dashboard.tabPortfolio', 'Portfolio')}
              </button>
            </div>
          )}

          <p className="text-[var(--color-secondary-text)] mb-4">
            {dashboardTab === 'portfolio' && showPortfolioTab
              ? tWithFallback(
                  t,
                  'dashboard.portfolioOverview',
                  "Portfolio companies you can access. Choose all companies for a combined view, or one company for that tenant's detail."
                )
              : t('dashboard.overview')}
          </p>

      {dashboardTab === 'firm' && (
        <>
      {/* Stats Grid - Compact (if enabled) */}
      {isWidgetEnabled('stats_grid') && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {stats.map((stat) => (
                <Card 
                  key={stat.name} 
                  className="group hover:bg-[var(--color-accent-bg)] hover:bg-opacity-20 transition-colors bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] border-0 shadow-none cursor-pointer"
                  onClick={() => stat.link && navigate(stat.link)}
                >
                  <CardContent className="px-4 py-2">
                <div className="flex items-center justify-between">
                  <div>
                        <p className="text-sm font-medium text-[var(--color-surface-text)] group-hover:text-[var(--color-accent-bg)] transition-colors">{stat.name}</p>
                        <p className="text-xl font-bold text-[var(--color-surface-text)] group-hover:text-[var(--color-accent-bg)] transition-colors">{stat.value}</p>
                        <div className="flex items-center mt-0.5">
                          <span className={`text-sm transition-colors ${
                        stat.changeType === 'positive' ? 'text-[var(--color-success)]' : 
                        stat.changeType === 'negative' ? 'text-[var(--color-error)]' : 'text-[var(--color-surface-text)] group-hover:text-[var(--color-accent-bg)]'
                      }`}>
                        {stat.change}
                      </span>
                          <span className="text-xs text-[var(--color-surface-text)] opacity-75 ml-1 group-hover:text-[var(--color-accent-bg)] group-hover:opacity-100 transition-colors">
                        {t('dashboard.fromLastMonth')}
                      </span>
                    </div>
                  </div>
                      <div className="p-2 rounded-lg bg-[var(--color-accent-bg)]">
                        <stat.icon className="h-5 w-5 text-[var(--color-accent-text)]" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/*
        Firm dashboard layout (common pattern: KPI strip + equal “module” columns):
        - Optional stats row: 4 metric tiles (responsive: 1 → 2 → 4 cols).
        - Widget row: 4 same-width cards — Agenda + Key Risks + Key Issues + Key Actions (1 col mobile, 2×2 tablet, 4 across desktop).
        Each module follows the same Card density (~3 items + primary “view all” action) as the key-* widgets.
      */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        <div className="min-h-0 min-w-0">
          <DashboardAgendaWidget compact maxItems={3} />
        </div>

      {/* Dashboard Customization Panel - HIDDEN: Reserved for future use */}
      {/* 
      {isCustomizing && (
        <div className="mb-6 p-6 bg-[var(--color-surface-bg)] border border-[var(--color-border)] rounded-lg">
          <div className="flex items-center space-x-2 mb-4">
            <AdjustmentsHorizontalIcon className="h-5 w-5 text-[var(--color-accent-text)]" />
            <h3 className="text-lg font-semibold text-[var(--color-surface-text)]">Dashboard Customization</h3>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div>
              <h4 className="text-sm font-medium text-[var(--color-surface-text)] mb-3">Layout</h4>
              <div className="space-y-2">
                {availableLayouts.map((layout) => (
                  <label key={layout.id} className="flex items-center space-x-3 p-3 border border-[var(--color-border)] rounded-lg hover:bg-[var(--color-accent-bg)] hover:bg-opacity-20 hover:text-[var(--color-accent-text)] transition-colors cursor-pointer text-[var(--color-surface-text)]">
                    <input
                      type="radio"
                      name="layout"
                      value={layout.id}
                      checked={dashboardPreferences.layout === layout.id}
                      onChange={(e) => updateLayout(e.target.value)}
                      className="text-[var(--color-accent-text)] focus:ring-[var(--color-accent-text)]"
                    />
                    <div className="flex-1">
                      <div className="font-medium">{layout.name}</div>
                      <div className="text-sm opacity-75">{layout.description}</div>
                    </div>
                    <div className="flex space-x-1">
                      {Array.from({ length: layout.gridCols * layout.gridRows }).map((_, i) => (
                        <div key={i} className="w-2 h-2 bg-[var(--color-accent-text)] rounded-sm"></div>
                      ))}
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <h4 className="text-sm font-medium text-[var(--color-surface-text)] mb-3">Widgets</h4>
              <div className="space-y-2">
                {dashboardPreferences.widgets.filter((widget) => widget.component !== 'kpi-layout-prefs' && widget.component !== 'kpi-perf-prefs').map((widget) => (
                  <label key={widget.id} className="flex items-center space-x-3 p-3 border border-[var(--color-border)] rounded-lg hover:bg-[var(--color-accent-bg)] hover:bg-opacity-20 hover:text-[var(--color-accent-text)] transition-colors cursor-pointer text-[var(--color-surface-text)]">
                    <input
                      type="checkbox"
                      checked={widget.enabled}
                      onChange={() => toggleWidget(widget.id)}
                      className="text-[var(--color-accent-text)] focus:ring-[var(--color-accent-text)]"
                    />
                    <div className="flex-1">
                      <div className="font-medium">{widget.name}</div>
                      <div className="text-sm opacity-75">Component: {widget.component}</div>
                    </div>
                    <div className={`px-2 py-1 rounded-full text-xs ${
                      widget.enabled 
                        ? 'bg-[var(--color-success-bg)] text-[var(--color-success-text)]' 
                        : 'bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)]'
                    }`}>
                      {widget.enabled ? 'Enabled' : 'Disabled'}
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <h4 className="text-sm font-medium text-[var(--color-surface-text)] mb-3">Spacing</h4>
              <div className="space-y-2">
                {[
                  { id: 'compact', name: 'Compact', description: 'Tight spacing between widgets' },
                  { id: 'comfortable', name: 'Comfortable', description: 'Balanced spacing (default)' },
                  { id: 'spacious', name: 'Spacious', description: 'Generous spacing between widgets' }
                ].map((spacing) => (
                  <label key={spacing.id} className="flex items-center space-x-3 p-3 border border-[var(--color-border)] rounded-lg hover:bg-[var(--color-accent-bg)] hover:bg-opacity-20 hover:text-[var(--color-accent-text)] transition-colors cursor-pointer text-[var(--color-surface-text)]">
                    <input
                      type="radio"
                      name="spacing"
                      value={spacing.id}
                      checked={dashboardPreferences.spacing === spacing.id}
                      onChange={(e) => updateSpacing(e.target.value as 'compact' | 'comfortable' | 'spacious')}
                      className="text-[var(--color-accent-text)] focus:ring-[var(--color-accent-text)]"
                    />
                    <div className="flex-1">
                      <div className="font-medium">{spacing.name}</div>
                      <div className="text-sm opacity-75">{spacing.description}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {isCustomizing && (
        <div className="mb-4 p-3 bg-yellow-100 border border-yellow-300 rounded-lg">
          <p className="text-sm text-yellow-800">
            <strong>Debug Info:</strong> Layout: {dashboardPreferences.layout} | Spacing: {dashboardPreferences.spacing} | 
            Grid Classes: {getLayoutClass(dashboardPreferences.layout)} | Gap Classes: {getSpacingClass(dashboardPreferences.spacing)}
          </p>
        </div>
      )}
      */}

        {/* Key Risks */}
        {isWidgetEnabled('key_risks') && (
          <div className="min-h-0 min-w-0">
            <KeyRisksWidget tenantId={user?.tenantId || ''} limit={3} />
          </div>
        )}

        {/* Key Issues */}
        {isWidgetEnabled('key_issues') && (
          <div className="min-h-0 min-w-0">
            <KeyIssuesWidget tenantId={user?.tenantId || ''} limit={3} />
          </div>
        )}

        {/* Key Actions */}
        {isWidgetEnabled('key_actions') && (
          <div className="min-h-0 min-w-0">
            <KeyActionsWidget tenantId={user?.tenantId || ''} limit={3} />
          </div>
        )}

        {/* Recent Plans - HIDDEN */}
      </div>
      </>
      )}

      {dashboardTab === 'portfolio' && showPortfolioTab && (
        <>
          {portfolioCompaniesLoading ? (
            <div className="flex flex-col items-center justify-center py-16 text-[var(--color-secondary-text)]">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--color-accent-text)] mb-3" />
              <p className="text-sm">{t('common.loading')}</p>
            </div>
          ) : portfolioCompanies.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-4 text-center border border-dashed border-[var(--color-border)] rounded-lg bg-[var(--color-secondary-bg)]/30">
              <BuildingOffice2Icon className="h-14 w-14 opacity-40 mb-4 text-[var(--color-secondary-text)] mx-auto" />
              <h3 className="text-lg font-semibold text-[var(--color-surface-text)]">
                {t('pePortfolio.dashboard.emptyScopeTitle')}
              </h3>
              <p className="text-sm text-[var(--color-secondary-text)] mt-2 max-w-md">
                {t('pePortfolio.dashboard.emptyScopeBody')}
              </p>
            </div>
          ) : (
            <>
              <div className="mb-4 md:flex md:items-center md:gap-3">
                <label htmlFor="portfolio-dashboard-scope" className="sr-only">
                  {tWithFallback(t, 'pePortfolio.dashboard.selectCompanyScope', 'Portfolio company scope')}
                </label>
                <select
                  id="portfolio-dashboard-scope"
                  value={portfolioCompanyId}
                  onChange={(e) => setPortfolioCompanyId(e.target.value)}
                  className="w-full max-w-md rounded-md border border-[var(--color-border)] bg-[var(--color-surface-bg)] px-3 py-2 text-sm text-[var(--color-surface-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent-text)]"
                >
                  <option value="__all__">
                    {tWithFallback(t, 'pePortfolio.dashboard.allCompanies', 'All companies')}
                  </option>
                  {portfolioCompanies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <PortfolioDashboardPanel
                bundle={portfolioBundle}
                loading={portfolioDashboardLoading}
                error={portfolioDashboardError}
                aggregateMode={portfolioCompanyId === '__all__'}
                onRetry={retryPortfolioDashboard}
                statsEnabled={isWidgetEnabled('stats_grid')}
                keyRisksEnabled={isWidgetEnabled('key_risks')}
                keyIssuesEnabled={isWidgetEnabled('key_issues')}
                keyActionsEnabled={isWidgetEnabled('key_actions')}
                interactiveNavigationEnabled={false}
              />
            </>
          )}
        </>
      )}
        </div>
      </div>

      {/* Setup Wizard Modal */}
      {showSetupWizard && (
        <SetupWizardModal
          isOpen={showSetupWizard}
          onClose={() => {
            setShowSetupWizard(false);
            setShouldShowSetupWizard(false);
          }}
          setupType={setupType}
          onComplete={() => {
            setShowSetupWizard(false);
            setShouldShowSetupWizard(false);
            checkSetupStatus();
          }}
        />
      )}
    </div>
  );
};

export default DashboardPage; 